import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import { useMutation } from '@tanstack/react-query';

import FilterBar from 'components/shared/filters/FilterBar';
import FilterMultiSelect from 'components/shared/filters/FilterMultiSelect';
import { Select } from 'components/shared/form/InfiniteScrollDropdown';
import {
  ADDITIONAL_FILTERS,
  COMANAGEMENT_TEAM,
  createClientDropdown,
  createGroupTypeDropdown,
  DISCHARGE_REASON_DROPDOWN,
  EPISODE_TYPE_DROPDOWN,
  FILTER_TYPES,
  HOSPITAL_DROPDOWN,
  PAYER_DROPDOWN,
  PHYSICIAN_GROUP_DROPDOWN,
  PHYSICIAN_TEAM_DROPDOWN,
  PLAN_TYPE_DROPDOWN,
  REFERRAL_STATUS_DROPDOWN,
  sortFilterDropdowns,
} from 'constants/filterConfigs';
import { OWNER_CLIENT } from 'constants/filterKeysConstants';
import { SKILLED_NURSING_FACILITY } from 'constants/locationTypes';
import { useProfile } from 'context/profile';
import FilterValueArray from 'models/filterValues/FilterValueArray';
import GroupType from 'models/GroupType';
import { DischargeCalendarFilterValue } from 'models/userPreferences/DischargeCalendarFilter';
import { upsertDischargeCalendarFilter } from 'services/api/preferences';
import {
  DischargeCalendarFilterState,
  DischargeCalendarView,
  useDischargeCalendarCurrentView,
  useDischargeCalendarFilters,
  useDischargeCalendarFiltersActions,
} from 'stores/dischargeCalendarStore';
import { FilterOption } from 'stores/types';

import { DISCHARGE_CALENDAR_ROUTES, DISCHARGE_CALENDAR_VIEWS } from './constants';

const VIEW_OPTIONS: { value: DischargeCalendarView; label: string }[] = [
  { value: DISCHARGE_CALENDAR_VIEWS.DAY, label: 'Day' },
  { value: DISCHARGE_CALENDAR_VIEWS.MONTH, label: 'Month' },
  { value: DISCHARGE_CALENDAR_VIEWS.WEEK, label: 'Week' },
];

const SNF_FILTER_KEY = 'skilledNursingFacility';

interface DischargeCalendarFilterBarProps {
  onApplyFilters: (filters: DischargeCalendarFilterState & { search?: string }) => void;
}

export default function DischargeCalendarFilterBar({ onApplyFilters }: DischargeCalendarFilterBarProps) {
  const { profile } = useProfile();
  const navigate = useNavigate();

  const filters = useDischargeCalendarFilters();
  const currentView = useDischargeCalendarCurrentView();
  const { setFilter, setFilters, setCurrentView, clearFilters } = useDischargeCalendarFiltersActions();

  const { mutate: savePreferences } = useMutation({
    mutationFn: upsertDischargeCalendarFilter,
  });

  const snfGroupType = useMemo(() => {
    const snf = profile.tabProviderTypes.find((gt) => gt.apiName === SKILLED_NURSING_FACILITY);
    return snf ?? new GroupType({ apiName: SKILLED_NURSING_FACILITY, displayName: 'SNF' });
  }, [profile.tabProviderTypes]);

  const snfFilterConfig = useMemo(
    () => ({
      ...createGroupTypeDropdown(snfGroupType, { include: 'groupType' }),
      filterType: FILTER_TYPES.DROPDOWN,
    }),
    [snfGroupType]
  );

  const filterSections = useMemo(
    () => [
      {
        title: COMANAGEMENT_TEAM,
        filters: sortFilterDropdowns([
          { ...HOSPITAL_DROPDOWN, filterType: FILTER_TYPES.DROPDOWN },
          ...(profile.canActAsProvider
            ? [
                {
                  ...createClientDropdown([], 'Organization', OWNER_CLIENT, {
                    viewOnly: false,
                    groupTypeType: 'manager',
                  }),
                  filterType: FILTER_TYPES.DROPDOWN,
                },
              ]
            : []),
          { ...PAYER_DROPDOWN, filterType: FILTER_TYPES.DROPDOWN },
          { ...PHYSICIAN_TEAM_DROPDOWN, filterType: FILTER_TYPES.DROPDOWN },
          { ...PHYSICIAN_GROUP_DROPDOWN, filterType: FILTER_TYPES.DROPDOWN },
          {
            ...createGroupTypeDropdown(snfGroupType, { include: 'groupType' }),
            filterType: FILTER_TYPES.DROPDOWN,
            placeholder: 'SNF',
          },
        ]),
      },
      {
        title: ADDITIONAL_FILTERS,
        filters: [
          { ...EPISODE_TYPE_DROPDOWN, filterType: FILTER_TYPES.DROPDOWN },
          { ...PLAN_TYPE_DROPDOWN, filterType: FILTER_TYPES.DROPDOWN },
          { ...DISCHARGE_REASON_DROPDOWN, filterType: FILTER_TYPES.DROPDOWN, placeholder: 'Planning Discharge To' },
          {
            ...REFERRAL_STATUS_DROPDOWN,
            filterType: FILTER_TYPES.DROPDOWN,
            placeholder: 'Referral Status',
            name: 'referralStatus',
          },
        ],
      },
    ],
    [snfGroupType, profile.canActAsProvider]
  );

  const handleSnfFilterChange = (values: FilterValueArray) => {
    setFilter(SNF_FILTER_KEY, values);
    const updatedFilters = { ...filters, [SNF_FILTER_KEY]: values };
    savePreferences({
      clientId: profile.actingClient.id,
      value: { ...updatedFilters, currentView } as DischargeCalendarFilterValue,
    });
  };

  const handleViewChange = (view: DischargeCalendarView) => {
    setCurrentView(view);
    const route = DISCHARGE_CALENDAR_ROUTES[view.toUpperCase() as keyof typeof DISCHARGE_CALENDAR_ROUTES];
    navigate(route);
    savePreferences({
      clientId: profile.actingClient.id,
      value: { ...filters, currentView: view } as DischargeCalendarFilterValue,
    });
  };

  const handleApplyFilters = (updatedFilters: DischargeCalendarFilterState & { search?: string }) => {
    const { search: _search, ...rest } = updatedFilters;
    setFilters(rest);
    onApplyFilters(updatedFilters);
    savePreferences({
      clientId: profile.actingClient.id,
      value: { ...rest, currentView } as DischargeCalendarFilterValue,
    });
  };

  const handleClearFilters = () => {
    clearFilters();
    savePreferences({
      clientId: profile.actingClient.id,
      value: { currentView } as DischargeCalendarFilterValue,
    });
  };

  return (
    <FilterBar
      applyFilters={handleApplyFilters}
      filters={filters}
      filterSections={filterSections}
      onClearFilters={handleClearFilters}
      title='Discharge Calendar'
      resultsText='patients'
      searchPlaceholder='Search Patients'
      showResults={false}
      quickFilterShowMinWidth={1100}
      quickFilter={
        <FilterMultiSelect
          {...snfFilterConfig}
          placeholder='SNF'
          onChange={handleSnfFilterChange}
          value={filters[SNF_FILTER_KEY] as FilterOption[]}
          debounceTime={500}
        />
      }
      callToActionShowMinWidth={900} // dropdown styling gets weird between 900 and mobile
      callToAction={
        <Select
          options={VIEW_OPTIONS}
          value={VIEW_OPTIONS.find((opt) => opt.value === currentView) ?? null}
          onChange={(option) => handleViewChange(option.value)}
          getOptionValue={(opt) => opt.value}
          getOptionLabel={(opt) => opt.label}
        />
      }
    />
  );
}
